import createQuoteLineId from "../../lib/createQuoteLineId";
import { AddCircle, Delete, DirectionsRun, MoneyOff, Sell, Title } from "@mui/icons-material";
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddItemModal from "./AddItemModal";

const getLineId = (l) => l?._id || l?.clientId;

function computeItemExt(line) {
  const quantity = Number(line.quantity) || 0;
  const cost = Number(line.cost) || 0;
  const price = Number(line.price) || 0;

  return {
    extCost: quantity * cost,
    extPrice: quantity * price,
  };
}

function recomputeSectionMath(lines) {
  const next = [...(lines || [])];

  const findHeaderAbove = (idx) => {
    for (let i = idx - 1; i >= 0; i--) if (next[i]?.type === "header") return i;
    return -1;
  };

  const findNearestTotalAboveInSection = (headerIdx, idx) => {
    for (let i = idx - 1; i > headerIdx; i--) {
      if (next[i]?.type === "subtotal" || next[i]?.type === "runningsubtotal") return i;
    }
    return -1;
  };

  const sumPrice = (arr) =>
    arr.reduce((sum, l) => {
      if (l?.type === "item") return sum + (Number(l.extPrice) || 0);
      if (l?.type === "discount") return sum + (Number(l.extPrice) || 0); // already negative
      return sum;
    }, 0);

  // 1) Normalize item ext fields
  for (let i = 0; i < next.length; i++) {
    if (next[i]?.type !== "item") continue;

    const qty = Number(next[i].quantity) || 0;
    const cost = Number(next[i].cost) || 0;
    const price = Number(next[i].price) || 0;

    next[i] = { ...next[i], extCost: qty * cost, extPrice: qty * price };
  }

  // 2) Compute discount / subtotal / runningsubtotal
  for (let i = 0; i < next.length; i++) {
    const row = next[i];

    // Ã¢Å“â€¦ GLOBAL running subtotal: everything above it regardless of sections
    if (row?.type === "runningsubtotal") {
      const runningPriceGlobal = sumPrice(next.slice(0, i));

      next[i] = {
        ...row,
        isSubtotal: true,
        extCost: 0,
        extPrice: Number(runningPriceGlobal.toFixed(2)),
      };
      continue;
    }

    // section-based subtotal/discount
    if (row?.type !== "discount" && row?.type !== "subtotal") continue;

    const headerIdx = findHeaderAbove(i);
    if (headerIdx === -1) {
      next[i] = { ...row, extCost: 0, extPrice: 0 };
      continue;
    }

    const sectionSlice = next.slice(headerIdx + 1, i);
    const sectionRunningPrice = sumPrice(sectionSlice);

    if (row.type === "discount") {
      const t = row.discountType || "percent";
      const v = Number(row.discountValue) || 0;

      // Ã¢Å“â€¦ prefer nearest subtotal/runningsubtotal ABOVE this discount within the section
      const nearestTotalIdx = findNearestTotalAboveInSection(headerIdx, i);

      const basePrice =
        nearestTotalIdx !== -1
          ? (Number(next[nearestTotalIdx]?.extPrice) || 0)
          : sectionRunningPrice;

      const discountAmount = t === "percent" ? basePrice * (v / 100) : v;

      next[i] = {
        ...row,
        isDiscount: true,
        extCost: 0,
        extPrice: -Math.abs(Number(discountAmount.toFixed(2))),
      };
      continue;
    }

    if (row.type === "subtotal") {
      next[i] = {
        ...row,
        isSubtotal: true,
        extCost: 0,
        extPrice: Number(sectionRunningPrice.toFixed(2)),
      };
      continue;
    }
  }

  return next;
}

const QuoteItems = ({ quote, patchQuote, onAddProducts, readOnly = false }) => {
    const [itemModal, setOpenItemModal] = useState(false)
    const [totalCost,setTotalCost] = useState("")
    const [totalPrice,setTotalPrice] = useState("")
    const [totalProfit,setTotalProfit] = useState("")
    const [descEditor, setDescEditor] = useState({
        open: false,
        rowId: null,     // _id || clientId
        idx: -1,         // index in quote.lines
        title: "",
        body: "",
    });
    const [discountModal, setDiscountModal] = useState({
        open: false,
        type: "percent",  // "percent" | "amount"
        value: 10,        // number
        label: "Discount",
    });

    const handleOpenItemModal = () => setOpenItemModal(true)
    const handleCloseItemModal = () => setOpenItemModal(false)    

    const quoteRef = useRef(quote);
    quoteRef.current = quote;

    const openDescriptionEditor = (rowId) => {
        const lines = quoteRef.current?.lines || [];
        const idx = lines.findIndex((l) => String(getLineId(l)) === String(rowId));
        if (idx === -1) return;

        setDescEditor({
            open: true,
            rowId,
            idx,
            title: lines[idx]?.descriptionTitle || "",
            body: lines[idx]?.descriptionBody || "",
        });
    };

    useEffect(() => {
        
        const tCost = quote?.lines.filter(l => l.type !== "subtotal").reduce(
            (sum, line) => sum + (Number(line.extCost) || 0),
            0
        );        
        const tPrice = quote?.lines.filter(l => l.type !== "subtotal" && l.type !== "runningsubtotal").reduce(
            (sum, line) => sum + (Number(line.extPrice) || 0),
            0
        );        

        const tProfit = tPrice - tCost

        setTotalCost(new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(tCost) )  
        setTotalPrice(new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(tPrice) )
        setTotalProfit(new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(tProfit) )          

    }, [quote]);    

    const handleDeleteRow = useCallback((lineId) => {
        const lines = Array.isArray(quoteRef.current?.lines) ? quoteRef.current.lines : [];

        const remaining = lines.filter((l) => String(l._id || l.clientId) !== String(lineId));

        const recomputed = recomputeSectionMath(remaining);
        const renumbered = recomputed.map((l, idx) => ({ ...l, lineNumber: idx + 1 }));

        patchQuote({ lines: renumbered });
    }, [patchQuote]);

    const handleRowOrderChange = useCallback(({ row, targetIndex }) => {
        if (readOnly) return;
        const lines = quoteRef.current?.lines || [];
        const index = lines.findIndex(line => String(getLineId(line)) === String(getLineId(row)));
        if (index < 0 || !Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= lines.length || index === targetIndex) return;
        const next = [...lines];
        const [moved] = next.splice(index, 1);
        next.splice(targetIndex, 0, moved);
        const reordered = recomputeSectionMath(next).map((line, i) => ({ ...line, lineNumber: i + 1 }));
        quoteRef.current = { ...quoteRef.current, lines: reordered };
        patchQuote({ lines: reordered });
    }, [patchQuote, readOnly]);

    const columns = useMemo(
        () => [
        { field: "lineNumber", headerName: "Line", width: 90 },
        { field: "quantity", headerName: "Qty", width: 90, type: "number", editable: true },
        { field: "manufacturerPartNumber", headerName: "Part #", width: 140, editable: true },
        {
            field: "descriptionTitle",
            headerName: "Description",
            flex: 1,
            minWidth: 260,
            editable: false,
            renderCell: (params) => {
                const row = params.row;
                const rowId = getLineId(row);

                return (
                <Box
                    sx={{
                        height: "100%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",   // Ã¢Å“â€¦ vertical centering
                        px: 1.5,                // consistent horizontal padding
                        cursor: "pointer",
                        overflow: "hidden",
                    }}
                    onClick={() => openDescriptionEditor(rowId)}
                >
                    <Typography
                    sx={{
                        fontSize: "0.875rem",   // Ã¢Å“â€¦ MATCH DataGrid default
                        fontWeight: row.type === "header" ? 700 : 400,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                    }}                    
                    >
                    {row.descriptionTitle || ""}
                    </Typography>

                    {/* optional preview of body */}
                    {row.descriptionBody ? (
                    <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.75, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                        {row.descriptionBody}
                    </Typography>
                    ) : null}
                </Box>
                );
            },
        },

        {
            field: "cost",
            headerName: "Unit Cost",
            width: 140,
            type: "number",
            editable: true,
            renderCell: ({ row: { cost } }) => (
                <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                }}
                >
                <Typography sx={{ color: '#592e7dff', fontWeight: 500, fontSize: '0.875rem' }}>
                    {cost
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        }).format(cost)
                    : null}
                </Typography>
                </Box>
            ),             
        },
        {
            field: "extCost",
            headerName: "Ext Cost",
            width: 140,
            type: "number",
            editable: false,
            renderCell: ({ row: { extCost } }) => (
                <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                }}
                >
                <Typography sx={{ color: '#592e7dff', fontWeight: 500, fontSize: '0.875rem' }}>
                    {extCost
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        }).format(extCost)
                    : null}
                </Typography>
                </Box>
            ),             
        },      
        {
            field: "price",
            headerName: "Unit Price",
            width: 140,
            type: "number",
            editable: true,
            renderCell: ({ row: { price } }) => (
                <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                }}
                >
                <Typography sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.875rem' }}>
                    {price
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        }).format(price)
                    : null}
                </Typography>
                </Box>
            ),             
            // valueFormatter: (v) => {
            //     const val = typeof v === "object" ? v?.value : v;
            //     return typeof val === "number"
            //     ? val.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
            //     : "";
            // }
        },
        {
            field: "extPrice",
            headerName: "Ext Price",
            width: 140,
            type: "number",
            editable: false,
            renderCell: ({ row: { extPrice } }) => (
                <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                }}
                >
                <Typography sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.875rem' }}>
                    {extPrice
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        }).format(extPrice)
                    : null}
                </Typography>
                </Box>
            ),             
            // valueFormatter: (v) => {
            //     const val = typeof v === "object" ? v?.value : v;
            //     return typeof val === "number"
            //     ? val.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
            //     : "";
            // }
        },                   
        {
            field: "",
            headerName: "",
            renderCell: ({ row: { _id, quantity, descriptionTitle, manufacturerPartNumber, clientId } }) => (
                <Tooltip title={quantity ? `Remove (${quantity}) ${manufacturerPartNumber}` : `Remove ${descriptionTitle}`}>
                    <IconButton className="hive-quote-items-delete" onClick={() => handleDeleteRow(_id || clientId)}>
                        <Delete color="error"/>
                    </IconButton>
                </Tooltip>
            )
        }         
        ],
        [handleDeleteRow]
    );

    const handleBlankLine = () => {
        const lines = Array.isArray(quote?.lines) ? quote.lines : [];

        const nextLineNumber =
            lines.length === 0
            ? 1
            : Math.max(...lines.map((l) => Number(l.lineNumber) || 0)) + 1;

        const newLine = {
            _id: createQuoteLineId(),
            lineNumber: nextLineNumber,
            type: "header",
            // quantity: 0,
            // unitPrice: 0,
            // extPrice: 0,
            descriptionTitle: "",
            descriptionBody: "",
            manufacturerPartNumber: "",
            productId: null,
            isSubtotal: false,
            isDiscount: false,
        };

        patchQuote({ lines: [...lines, newLine] });
    };

    const handleAddSubtotal = () => {
        const lines = Array.isArray(quoteRef.current?.lines) ? quoteRef.current.lines : [];

        const newSubtotal = {
            _id: createQuoteLineId(),
            type: "subtotal",
            descriptionTitle: "Subtotal",
            descriptionBody: "",
            isSubtotal: true,
            isDiscount: false,
            extCost: 0,
            extPrice: 0,
        };

        const next = [...lines, newSubtotal];
        const recomputed = recomputeSectionMath(next);
        const renumbered = recomputed.map((l, i) => ({ ...l, lineNumber: i + 1 }));

        patchQuote({ lines: renumbered });
    };

    const handleAddRunningSubtotal = () => {
        const lines = Array.isArray(quoteRef.current?.lines) ? quoteRef.current.lines : [];

        const newRunningSubtotal = {
            _id: createQuoteLineId(),
            type: "runningsubtotal",
            descriptionTitle: "Running Subtotal",
            descriptionBody: "",
            isSubtotal: true,     // optional, but keep consistent if you use these flags
            isDiscount: false,
            extCost: 0,
            extPrice: 0,
        };

        const next = [...lines, newRunningSubtotal];
        const recomputed = recomputeSectionMath(next);
        const renumbered = recomputed.map((l, i) => ({ ...l, lineNumber: i + 1 }));

        patchQuote({ lines: renumbered });
    };

    const openAddDiscount = () => {
        setDiscountModal({
            open: true,
            type: "percent",
            value: 10,
            label: "Customer Concession",
        });
    };

    const handleSaveDiscount = () => {
        const lines = Array.isArray(quoteRef.current?.lines) ? quoteRef.current.lines : [];

        const newDiscount = {
            _id: createQuoteLineId(),
            type: "discount",
            descriptionTitle: discountModal.label || "Discount",
            descriptionBody: "",
            discountType: discountModal.type,
            discountValue: Number(discountModal.value) || 0,
            extCost: 0,
            extPrice: 0,          // computed in recomputeSectionMath (negative)
            isDiscount: true,
            isSubtotal: false,
        };

        const next = [...lines, newDiscount];

        const recomputed = recomputeSectionMath(next);
        const renumbered = recomputed.map((l, i) => ({ ...l, lineNumber: i + 1 }));

        patchQuote({ lines: renumbered });

        setDiscountModal(s => ({ ...s, open: false }));
    };

    return (
        <Box className="hive-quote-items" sx={{ minHeight: 520, width: "100%" }}>
            <AddItemModal open={itemModal} handleClose={handleCloseItemModal} title={"Add Items To Quote"} onAddProducts={onAddProducts} />
           
            <Box
                className="hive-quote-items-toolbar"
                sx={{
                    position: "sticky",
                    top: 74,
                    zIndex: 10,
                    backgroundColor: "#f8f9fa",
                    px: 1,
                    py: 0.5,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 1,
                }}
            >
                {/* LEFT: Buttons (always one row) */}
                {!readOnly && (
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                    flexShrink: 0,
                    flexWrap: "nowrap",
                    overflowX: "auto",          // optional safety if you ever add more icons
                    WebkitOverflowScrolling: "touch",
                    }}
                >
                    <Tooltip title="Add Item(s)">
                    <IconButton className="hive-quote-items-action" color="primary" onClick={handleOpenItemModal}>
                        <AddCircle />
                    </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Subtotal">
                    <IconButton className="hive-quote-items-action" color="secondary" onClick={handleAddSubtotal}>
                        <Sell />
                    </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Running Subtotal">
                        <IconButton className="hive-quote-items-action" color="secondary" onClick={handleAddRunningSubtotal}>
                            <DirectionsRun />
                        </IconButton>
                    </Tooltip>                    

                    <Tooltip title="Add Discount">
                    <IconButton className="hive-quote-items-action" color="error" onClick={openAddDiscount}>
                        <MoneyOff />
                    </IconButton>
                    </Tooltip>

                    <Tooltip title="Add Header/Blank Line">
                    <IconButton className="hive-quote-items-action" color="primary" onClick={handleBlankLine}>
                        <Title />
                    </IconButton>
                    </Tooltip>


                </Stack>
                )}

                {/* RIGHT: Chips (stack on mobile, row on desktop) */}
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                    width: { xs: "100%", sm: "auto" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    ml: { sm: "auto" },         // pushes chips to the right on desktop
                    }}
                >
                    {!readOnly && (
                    <Chip
                    className="hive-quote-items-chip hive-quote-items-chip-cost"
                    label={`Total Cost: ${totalCost}`}
                    color="primary"
                    variant="outlined"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                    )}
                    <Chip
                    className="hive-quote-items-chip hive-quote-items-chip-price"
                    label={`Total Price: ${totalPrice}`}
                    color="secondary"
                    variant="outlined"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                    {!readOnly && (
                    <Chip
                    className="hive-quote-items-chip hive-quote-items-chip-profit"
                    label={`Total Profit: ${totalProfit}`}
                    color="success"
                    variant="outlined"
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                    />
                    )}
                </Stack>
            </Box>

            <DataGridPro
                className="hive-quote-items-grid"
                density="compact"
                rows={quote?.lines || []}
                getRowId={getLineId}
                columns={columns}
                columnVisibilityModel={{ cost: !readOnly, extCost: !readOnly, "": !readOnly }}
                isCellEditable={() => !readOnly}
                rowReordering={!readOnly}
                onRowOrderChange={readOnly ? undefined : handleRowOrderChange}
                disableRowSelectionOnClick
                // Ã¢Å“â€¦ disable sort/filter across the whole grid
                disableColumnFilter
                disableColumnSorting
                disableColumnMenu
                processRowUpdate={readOnly ? undefined : (newRow, oldRow) => {
                const lines = quoteRef.current?.lines || [];

                const rowId = getLineId(newRow);
                const idx = lines.findIndex(l => String(getLineId(l)) === String(rowId));
                if (idx === -1) return oldRow;

                // Non-item rows: keep your existing logic
                if (newRow.type !== "item") {
                    patchQuote({ [`lines.${idx}.descriptionTitle`]: newRow.descriptionTitle ?? "" });
                    return newRow;
                }

                // compute ext values from qty/cost/price
                const { extCost, extPrice } = computeItemExt(newRow);

                // apply edit to local copy
                const nextLines = [...lines];
                nextLines[idx] = {
                    ...nextLines[idx],
                    ...newRow,
                    quantity: Number(newRow.quantity) || 0,
                    cost: Number(newRow.cost) || 0,
                    price: Number(newRow.price) || 0,
                    extCost: Number(extCost.toFixed(2)),
                    extPrice: Number(extPrice.toFixed(2)),
                    manufacturerPartNumber: newRow.manufacturerPartNumber ?? "",
                    descriptionTitle: newRow.descriptionTitle ?? "",
                };

                // Ã¢Å“â€¦ FULL recompute (this is what makes discounts/subtotals consistent)
                const recomputed = recomputeSectionMath(nextLines);
                const renumbered = recomputed.map((l, i) => ({ ...l, lineNumber: i + 1 }));

                patchQuote({ lines: renumbered });

                // return immediate row values so grid updates right away
                return { ...newRow, extCost, extPrice };
                }}
                onProcessRowUpdateError={(err) => console.error(err)}
                experimentalFeatures={{ newEditingApi: true }}
                initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                }}
                pageSizeOptions={[25, 50, 100]}
                getRowClassName={(params) => {
                    const classes = [];

                    // -- Row type styling -- //
                    if (params.row.type === "subtotal") classes.push("subtotal-row");
                    if (params.row.type === "runningsubtotal") classes.push("running-subtotal-row");
                    if (params.row.type === "header") classes.push("hive-header-row");
                    if (params.row.type === "discount") classes.push("discount-row");

                    // -- Zebra striping (only for normal rows) -- //
                    if (!params.row.type) {
                        classes.push(
                            params.indexRelativeToCurrentPage % 2 === 0
                                ? "quote-row-even"
                                : "quote-row-odd"
                        );
                    }

                    return classes.join(" ");
                }}
                sx={{
                    backgroundColor: "white",
                    "& .quote-row-even": {
                    backgroundColor: "#fafafa",
                    },
                    "& .quote-row-odd": {
                    backgroundColor: "#ffffff",
                    },
                    '& .subtotal-row': {
                        backgroundColor: '#fff4e5', // Light blue
                        fontWeight: 'bold',
                        borderTop: '1px solid #ffd199',
                        // borderBottom: '1px solid #ffd199',
                    },     
                    '& .running-subtotal-row': {
                        backgroundColor: '#efe7ff',
                        fontWeight: 'bold',
                        borderTop: '1px solid #d5b8ff',
                    },
                    '& .hive-header-row': {
                        backgroundColor: '#e6f2ff', // Your light blue for missing unit cost
                        fontWeight: 'bold',
                    },
                    '& .discount-row': {
                        backgroundColor: '#ffeaea',
                        fontWeight: 'bold',
                        borderTop: '1px solid #ffb3b3',
                    },                                                                     
                    marginTop: "1em",
                    height: "auto"
                }}                                
            />

            <Dialog
                className="hive-quote-items-dialog"
                open={descEditor.open}
                onClose={() => setDescEditor(s => ({ ...s, open: false }))}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>{readOnly ? "View Description" : "Edit Description"}</DialogTitle>

                <DialogContent sx={{ paddingTop: 1 }}>
                    <TextField
                        sx={{marginTop: 1}}
                        label="Description"
                        fullWidth
                        multiline
                        minRows={4}
                        value={descEditor.title}
                        onChange={(e) => setDescEditor(s => ({ ...s, title: e.target.value }))}
                        InputProps={{ readOnly }}
                        helperText={readOnly ? "Read-only quote users can view the description here." : "Line breaks will be preserved in the PDF."}
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDescEditor(s => ({ ...s, open: false }))}>{readOnly ? "Close" : "Cancel"}</Button>

                    {!readOnly && <Button
                        variant="contained"
                        onClick={() => {
                            const idx = descEditor.idx;
                            if (idx < 0) return;

                            // Ã¢Å“â€¦ dot-path patches (no whole array rewrite)
                            patchQuote({
                                [`lines.${idx}.descriptionTitle`]: descEditor.title,
                                // [`lines.${idx}.descriptionBody`]: descEditor.body,
                            });

                            setDescEditor(s => ({ ...s, open: false }));
                        }}
                    >
                        Save
                    </Button>}
                </DialogActions>
            </Dialog>


            <Dialog
                className="hive-quote-items-dialog"
                open={!readOnly && discountModal.open}
                onClose={() => setDiscountModal(s => ({ ...s, open: false }))}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add Discount</DialogTitle>

                <DialogContent sx={{ paddingTop: 1 }}>
                    <TextField
                        fullWidth
                        label="Label"
                        value={discountModal.label}
                        onChange={(e) => setDiscountModal(s => ({ ...s, label: e.target.value }))}
                        sx={{ mb: 2, mt: 1 }}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Discount Type</InputLabel>
                        <Select
                            label="Discount Type"
                            value={discountModal.type}
                            onChange={(e) => setDiscountModal(s => ({ ...s, type: e.target.value }))}
                        >
                            <MenuItem value="percent">Percent (%)</MenuItem>
                            <MenuItem value="amount">Amount ($)</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        type="number"
                        label={discountModal.type === "percent" ? "Percent" : "Amount"}
                        value={discountModal.value}
                        onChange={(e) => setDiscountModal(s => ({ ...s, value: e.target.value }))}
                        inputProps={{
                            min: 0,
                            step: discountModal.type === "percent" ? 0.1 : 1,
                        }}
                        helperText={
                            discountModal.type === "percent"
                            ? "Example: 10 = 10% off the section total above the discount row."
                            : "Example: 250 = $250 off the section total above the discount row."
                        }
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDiscountModal(s => ({ ...s, open: false }))}>Cancel</Button>
                    {!readOnly && <Button variant="contained" onClick={handleSaveDiscount}>Add</Button>}
                </DialogActions>
                
            </Dialog>



        </Box>        
    )

}

export default QuoteItems
